<template>
    <v-input :rules="fileValidationRules" :model-value="modelValue">
        <div class="mb-2 flex-grow-1">
            <v-file-upload
                :id="id"
                v-model="modelValue"
                :density="density"
                :show-size="showSize"
                :title="title"
                :clearable="clearable"
                :disabled="disabled || isLoading"
                :max-size="maxSize"
                :accept="accept"
                multiple
                @update:model-value="handleFileUpload"
            />
            <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mt-2" />
        </div>
    </v-input>
    <p class="text-caption mb-2">{{ maxSizeMb }} {{ t('form.fileUploadLimits') }}</p>
    <notification-bar
        v-model:show="notificationBarState.show"
        :color="notificationBarState.color"
        :icon="notificationBarState.icon"
        :border="notificationBarState.border"
        :message="notificationBarState.message"
        :time-out="-1"
    />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { VFileUpload } from 'vuetify/labs/VFileUpload';
import { IFileUpload } from '../../types';
import { convertFilesWithBytes, rejectedFileNotification, validateFiles, getFileSize } from '../../helpers';
import { useNotification } from '../../composables';
import { NotificationBar } from '../../';

const modelValue = defineModel<File[]>('modelValue', { default: () => [] });

const props = withDefaults(defineProps<IFileUpload>(), {
    density: 'compact',
    accept: '.jpg,.jpeg,.png,.pdf',
    maxSize: 10485760,
    showSize: false,
    clearable: false
});

const { t } = useI18n();
const { notificationBarState, notificationError } = useNotification();
const isLoading = ref(false);
const maxSizeMb = getFileSize(props.maxSize);

const fileValidationRules = computed(() => {
    if (!props.required) return [];
    return [(v: File[]) => (v && v.length > 0) || t('validation.fieldRequired')];
});

const handleFileUpload = async (files: File[]): Promise<void> => {
    try {
        notificationBarState.value.show = false;

        isLoading.value = true;

        const { valid, rejected } = await validateFiles(
            files,
            props.accept,
            props.maxSize,
            t('validation.fileSizeExceeded'),
            t('validation.extensionNotSupported'),
            t('validation.maxCountError')
        );

        if (rejected.length) {
            notificationBarState.value = notificationError(rejectedFileNotification(rejected));
        }

        (modelValue.value as File[]) = await convertFilesWithBytes(valid);
    } catch (_error) {
        notificationBarState.value = notificationError(t('validation.filesFailed'));
    } finally {
        isLoading.value = false;
    }
};
</script>
